import * as THREE from "three";
import { RequestService } from "../../../Service/RequestService";
import { ICaptureConfig, ICaptureConfigData_V1, ICaptureConfigData_V2 } from "../../../Tools/Capture/CaptureConfig";
import { CaptureToolCameraManager } from "../../../Tools/Capture/Components/CaptureToolCameraManager";
import { IRingState } from "../../RingStateController/RingStateController";
import { RenderEngine } from "../core/RenderEngine";
import { LogoController } from "../LogoController";


export interface CaptureControllerArguments {
  renderEngine:RenderEngine,
  requestService:RequestService,
  logoController:LogoController,
  getRingState:()=>IRingState,
  saveRingConfiguration:()=>Promise<string>,
  onUpdate:()=>void
}

export class CaptureController {
  private _renderEngine:RenderEngine;
  private _modelContainer:HTMLDivElement;
  private _requestService:RequestService;
  private _logoController:LogoController;
  private _cameraManager:CaptureToolCameraManager;
  private _activeMediaRecorder:MediaRecorder;
  private _ringGroup:THREE.Group;
  private _getRingState:()=>IRingState;
  private _saveRingConfiguration:()=>Promise<string>;
  private _onUpdate:()=>void;

  private _canvasWidth:number = 500;
  private _canvasHeight:number = 500;
  private _dpr:number = 1;
  private _backgroundActive:boolean = true;
  private _reflectionActive:boolean = true;
  private _shadowActive:boolean = true;
  private _logoActive:boolean = true;
  private _reflectionPlaneRadius:number = 1;
  private _backgroundColor:string = "#FFFFFF";
  private _ringRotation:number[] = [0, 0, 0];
  private _videoLength:number = 5000;
  private _videoFps:number = 30;
  private _videoBitrate:number = 1000000;
  private _lastSavedConfigLink:string = null;

  public get cameraManager():CaptureToolCameraManager { return this._cameraManager };

  public get canvasWidth():number { return this._canvasWidth };
  public get canvasHeight():number { return this._canvasHeight };
  public get dpr():number { return this._dpr };
  public get backgroundActive():boolean { return this._backgroundActive }; 
  public get reflectionActive():boolean { return this._reflectionActive }; 
  public get shadowActive():boolean { return this._shadowActive }; 
  public get logoActive():boolean { return this._logoActive }; 
  public get reflectionPlaneRadius():number { return this._reflectionPlaneRadius };
  public get backgroundColor():string { return this._backgroundColor };
  public get ringRotation():number[] { return this._ringRotation };
  public get videoLength():number { return this._videoLength };
  public get videoFps():number { return this._videoFps };
  public get videoBitrate():number { return this._videoBitrate };
  public get lastSavedConfigLink():string { return this._lastSavedConfigLink };

  constructor(captureControllerArguments:CaptureControllerArguments) {
    this._renderEngine = captureControllerArguments.renderEngine;
    this._requestService = captureControllerArguments.requestService;
    this._logoController = captureControllerArguments.logoController;
    this._getRingState = captureControllerArguments.getRingState;
    this._saveRingConfiguration = captureControllerArguments.saveRingConfiguration;
    this._onUpdate = captureControllerArguments.onUpdate;
    this._cameraManager = new CaptureToolCameraManager(this._renderEngine, this._onUpdate);
  };

  public initialize = async (captureConfig:ICaptureConfig|string) => {
    const cameraController = this._renderEngine.engine.services.render.cameraController;
    const cameraProperties = cameraController.cameraProperties;

    this._modelContainer = this._renderEngine.engine.services.render.domContainer.mount.parentElement as HTMLDivElement;
    console.log("CaptureController._modelContainer", this._modelContainer);

    let meshes = await this._renderEngine.engine.services.render.getAllMeshesOfCurrentScene();
    meshes = meshes.filter((m) => m.userData && m.userData.ringSKU);
    this._ringGroup = meshes[0].parent.parent as THREE.Group;
    this._ringRotation = this.getRingRotation();

    const outerContainer = this._modelContainer.parentElement;
    this._modelContainer.style.backgroundColor = 'white';
    outerContainer.style.backgroundColor = 'lightGray';

    this.setCanvasWidth(this._canvasWidth);
    this.setCanvasHeight(this._canvasHeight);
    this.setReflectionPlaneRadius(this._reflectionPlaneRadius); //Just to turn reflectionPlane into CircleGeometry

    console.log(cameraController);

    this._cameraManager.createNewCamera();

    console.log(this._cameraManager.cameras);

    this._renderEngine.setCameraDistance(cameraProperties.distance_min);
    cameraProperties.distance_min = 0.01;
    cameraProperties.limits.up = 360;
    cameraProperties.limits.down = -360;
    cameraController.setOrbitCamera(cameraProperties);

    const camProps = Object.assign({}, cameraController.cameraProperties, { disable_pan: true });
    cameraController.setOrbitCamera(camProps);

    if (captureConfig) {
      let config;
      if (typeof captureConfig === "string") {
        config = await this.loadConfiguration(captureConfig);
      } else {
        config = captureConfig;
      }
      await this.setConfiguration(config);
    }

    return Promise.resolve();
  };

  public saveCaptureConfiguration = async (includeRingConfig?:boolean):Promise<string> => {
    const config = this.getConfiguration();

    const configId = await this._requestService.saveConfiguration(config);
    let ringConfigId = null;
    if (includeRingConfig) ringConfigId = await this._saveRingConfiguration();

    let link = window.location.href.split('?')[0];
    link += `?mode=capture`;
    link += `&captureConfig=${configId}`;
    if (ringConfigId) link += `&config=${ringConfigId}`;

    this._lastSavedConfigLink = link;
    this._onUpdate();

    return Promise.resolve(configId);
  };

  public getConfiguration = ():ICaptureConfig => {
    const configData:ICaptureConfigData_V2 = {
      canvasWidth: this._canvasWidth,
      canvasHeight: this._canvasHeight,
      backgroundActive: this._backgroundActive,
      reflectionActive: this._reflectionActive,
      shadowActive: this._shadowActive,
      logoActive: this._logoActive,
      backgroundColor: this._backgroundColor,
      reflectionPlaneRadius: this._reflectionPlaneRadius,
      ringRotation: this._ringRotation,
      videoLength: this._videoLength,
      videoFps: this._videoFps,
      videoBitrate: this._videoBitrate,
      cameras: this._cameraManager.cameras
    };

    const config:ICaptureConfig = {
      version: 2,
      data: configData
    };

    return config;
  }

  public loadConfiguration = async (configId:string):Promise<ICaptureConfig> => {
    const config = await this._requestService.loadConfiguration(configId) as ICaptureConfig;
    return config;
  }

  public setConfiguration = async (config:ICaptureConfig):Promise<void> => {
    console.warn("Loading saved capture tool configuration!", config);

    const loadConfig_V1 = (configData:ICaptureConfigData_V1) => {
      const cameraController = this._renderEngine.engine.services.render.cameraController;
      const { canvasWidth, canvasHeight, backgroundActive, reflectionActive, shadowActive, logoActive, cameraPosition } = configData;

      const cameraProperties = cameraController.cameraProperties;
      cameraProperties.position = [cameraPosition.x, cameraPosition.y, cameraPosition.z];

      return new Promise((resolve, reject) => {
        cameraController.setOrbitCamera(cameraProperties, { time: 2000 });

        this.setCanvasWidth(canvasWidth, true);
        this.setCanvasHeight(canvasHeight, true);
        this.setBackgroundActive(backgroundActive, true);
        this.setReflectionActive(reflectionActive, true);
        this.setShadowActive(shadowActive, true);
        this.setLogoActive(logoActive, true);

        this._onUpdate();
        resolve(null);
      })
    };

    const loadConfig_V2 = (configData:ICaptureConfigData_V2) => {
      const { canvasWidth, canvasHeight, backgroundActive, reflectionActive, shadowActive, logoActive, backgroundColor, reflectionPlaneRadius, ringRotation, videoLength, videoFps, videoBitrate, cameras } = configData;

      return new Promise((resolve, reject) => {
          this._cameraManager.cameras = cameras;
          this._cameraManager.changeSelectedCamera(cameras[0]);

          this.setCanvasWidth(canvasWidth, true);
          this.setCanvasHeight(canvasHeight, true);
          this.setBackgroundActive(backgroundActive, true);
          this.setReflectionActive(reflectionActive, true);
          this.setShadowActive(shadowActive, true);
          this.setLogoActive(logoActive, true);
          this.setBackgroundColor(backgroundColor, true);
          this.setReflectionPlaneRadius(reflectionPlaneRadius, true);
          this.setRingRotation(ringRotation, true);
          this.setVideoLength(videoLength, true);
          this.setVideoFps(videoFps, true);
          this.setVideoBitrate(videoBitrate, true);

          this._onUpdate();
          resolve(null);
      })
    };

    if (config.version === 1) {
      await loadConfig_V1(config.data as ICaptureConfigData_V1);
    } else if (config.version === 2) {
      await loadConfig_V2(config.data as ICaptureConfigData_V2);
    };

    console.warn("Loaded saved capture tool configuration!", config);

    return Promise.resolve();
  };

  public takeScreenshot = async (download:boolean = true):Promise<Blob> => {
    const ringState = this._getRingState();
    const screenshotBlob =  await this.getSingleScreenshot();
    if (download) this.downloadScreenshotBlob(screenshotBlob, ringState.ringModel.SKU);
    return Promise.resolve(screenshotBlob);
  };

  public getSingleScreenshot = ():Promise<Blob> => {
    return new Promise((resolve, reject) => {
      this._renderEngine.getScreenShot({dpr: this._dpr})
      .then((screenshotBlob) => {
        const image = new Image();
        image.src = URL.createObjectURL(screenshotBlob);
        image.addEventListener('load', (e) => {
          const canvas = document.createElement('canvas');
          const canvasContext = canvas.getContext('2d');
          canvas.width = image.width;
          canvas.height = image.height;

          canvasContext.drawImage(
            image,
            0,                                //sx
            0,                                //sy
            image.width,                      //sWidth
            image.height,                     //sHeight
            0,                                //dx
            0,                                //dy
            canvas.width,                     //dWidth
            canvas.height,                    //dHeight
          );

          canvas.toBlob((blob) => {
            resolve(blob)
          }, 'image/png', 1);
        });
      });
    });
  }

  public downloadScreenshotBlob = (screenshotBlob:Blob, name:string) => {
    const image = new Image();
    image.src = URL.createObjectURL(screenshotBlob);
    const link = document.createElement('a');
    link.href = image.src;
    link.target = '_blank';
    link.download = `${name}.png`;
    link.click();
  }

  public recordVideo = ():Promise<string> => {
    const ringState = this._getRingState();
    const canvas:HTMLCanvasElement = this._renderEngine.engine.services.render.domContainer.canvas;
    const recordedChunks = [];

    this.abortVideo();

    return new Promise((resolve, reject) => {
      const stream = (canvas as any).captureStream(this._videoFps);
      this._activeMediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm; codecs=vp9', bitsPerSecond: this._videoBitrate });

      this._activeMediaRecorder.start(1000);

      this._activeMediaRecorder.ondataavailable = (e) => {
        console.log("mediaRecorder.ondataavailable() called!");
        recordedChunks.push(e.data);
      };

      this._activeMediaRecorder.onstop = (e) => {
        const blob = new Blob(recordedChunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);

        console.log(blob, url);

        this.downloadVideoBlob(blob, ringState.ringModel.SKU);

        resolve(url);
      };

      window.setTimeout(() => {
        if (this._activeMediaRecorder) this._activeMediaRecorder.stop();
      }, this._videoLength);
    })
  }

  public abortVideo = () => {
    if (this._activeMediaRecorder && this._activeMediaRecorder.state !== "inactive") {
      this._activeMediaRecorder.onstop = null;
      this._activeMediaRecorder.stop();
    }
  };

  public downloadVideoBlob = (videoBlob:Blob, name:string) => {
    const video = document.createElement('video');
    video.src = URL.createObjectURL(videoBlob);
    const link = document.createElement('a');
    link.href = video.src;
    link.target = '_blank';
    link.download = `${name}.webm`;
    link.click();
  }

  public getRingRotation = ():number[] => {
    const rotation = this._ringGroup.rotation;
    return [THREE.MathUtils.radToDeg(rotation.x), THREE.MathUtils.radToDeg(rotation.y), THREE.MathUtils.radToDeg(rotation.z)];
  }

  public setRingRotation = (value:number[], silent = false) => {
    const rotation = this._ringGroup.rotation;
    rotation.x = THREE.MathUtils.degToRad(value[0]);
    rotation.y = THREE.MathUtils.degToRad(value[1]);
    rotation.z = THREE.MathUtils.degToRad(value[2]);
    this._ringRotation = value;
    if (!silent) this._onUpdate();
  }

  public setCanvasWidth = (value:number, silent = false) => {
    this._modelContainer.style.width = `${value}px`;
    this._modelContainer.style.maxWidth = `${value}px`;
    window.dispatchEvent(new Event('resize'));
    this._canvasWidth = value;
    if (!silent) this._onUpdate();
  }

  public setCanvasHeight = (value:number, silent = false) => {
    this._modelContainer.style.height = `${value}px`;
    this._modelContainer.style.maxHeight = `${value}px`;
    window.dispatchEvent(new Event('resize'));
    this._canvasHeight = value;
    if (!silent) this._onUpdate();
  }

  public setVideoLength = (value:number, silent = false) => {
    this._videoLength = value;
    if (!silent) this._onUpdate();
  }

  public setVideoFps = (value:number, silent = false) => {
    this._videoFps = value;
    if (!silent) this._onUpdate();
  }

  public setVideoBitrate = (value:number, silent = false) => {
    this._videoBitrate = value;
    if (!silent) this._onUpdate();
  }

  public setBackgroundColor = (value:string, silent = false) => {
    if (!value) value = "#FFFFFF";
    this._renderEngine.setBackground(new THREE.Color(value));
    this._backgroundColor = value;
    if (!silent) this._onUpdate();
  }

  public setReflectionPlaneRadius = (value:number, silent = false) => {
    if (!value) value = 1;
    this._renderEngine.reflectionPlane.geometry = new THREE.CircleGeometry(value, 64).rotateX(-Math.PI/2);
    this._reflectionPlaneRadius = value;
    if (!silent) this._onUpdate();
  }

  public setBackgroundActive = (value:boolean, silent = false) => {
    if (value) {
      this._renderEngine.setBackground(new THREE.Color( this._backgroundColor ));
    } else {
      this._renderEngine.setBackground(null);
      this.setReflectionActive(false, true); //Reflection does not work without background
    }
    this._backgroundActive = value;
    if (!silent) this._onUpdate();
  };

  public setReflectionActive = (value:boolean, silent = false) => {
    if (value) {
      this.setBackgroundActive(true, true); //Reflection need background to work
    }
    this._renderEngine.reflectionVisible = value;
    this._reflectionActive = value;
    if (!silent) this._onUpdate();
  };

  public setShadowActive = (value:boolean, silent = false) => {
    this._renderEngine.shadowVisible = value;
    this._shadowActive = value;
    if (!silent) this._onUpdate();
  };

  public setLogoActive = (value:boolean, silent = false) => {
    const ringState = this._getRingState();
    this._logoController.logoActive = value;
    this._logoController.updateLogo(ringState.width, ringState.ringModel.morphOptions);
    this._logoActive = value;
    if (!silent) this._onUpdate();
  };


}